<template>
  <OverlayLoader :show="loadModal" />
  <!--h4 class="text-success text-center">Api integrated</h4-->
  <div class="container-fluid px-4">
    <div class="mb-3">
      <BreadcrumbNav />
    </div>
    <MaterialButton @click="redirectToUsersSummary()">Users Summary</MaterialButton>
    <MaterialButton class="mx-2" @click="openAddUserPage()">Add User</MaterialButton>
    <MaterialButton class="mx-2" @click="showPeopleGroupsModal = true">People Groups</MaterialButton>
    <MaterialButton v-if="canAccessSetupWizard" class="mx-2" @click="openSetupWizardPage()">Setup Wizard</MaterialButton>
    <button class="btn-refresh mx-2" @click="refreshTable()"><i class="material-icons text-sm align-middle">refresh</i> Refresh</button>

    <!-- Filter bar -->
    <div class="d-flex flex-wrap align-items-center gap-2 mt-3 mb-1">
      <span class="text-sm text-secondary fw-semibold"><i class="material-icons text-sm align-middle me-1">filter_list</i>Filters:</span>
      <select class="form-select form-select-sm filter-select" v-model="filterAdmin">
        <option value="">Admin: All</option>
        <option value="yes">Admin: Yes</option>
        <option value="no">Admin: No</option>
      </select>
      <select class="form-select form-select-sm filter-select" v-model="filterVolunteer">
        <option value="">Volunteer: All</option>
        <option value="yes">Volunteer: Yes</option>
        <option value="no">Volunteer: No</option>
      </select>
      <button v-if="filterAdmin || filterVolunteer" class="btn btn-sm btn-outline-secondary m-0" @click="clearFilters">
        <i class="material-icons text-sm align-middle me-1">clear</i>Clear
      </button>
    </div>

    <BaseTable heading="Users" :rows="filteredUsers" :columns="columns" sortable searchable :selectable="false"
      :loading="loading" :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
      @searchChanged="onSearchChanged">
      <template #col-schedulingPreferences="{ row }">
        <button v-if="row.schedulingPreferences != 'none'" class="btn btn-sm btn-outline-info my-0"
          @click="openSchedule(row)">
          <i class="material-icons text-sm align-middle">event</i>
          {{ row.schedulingPreferences }}
        </button>

        <button v-else class="btn btn-sm p-0 my-0">
          {{ row.schedulingPreferences }}
        </button>
      </template>

      <template #col-volunteer="{ row }">
        <a
          v-if="row.volunteer && row.volunteer !== 'none' && row.volunteer !== 'None' && row.volunteer !== '-'"
          href="#"
          class="admin-link"
          @click.prevent="openVolunteerSummary(row)"
        >
          {{ row.volunteer }}
          <i class="material-icons admin-info-icon">summarize</i>
        </a>
        <span v-else>{{ row.volunteer || '-' }}</span>
      </template>

      <template #col-admin="{ row }">
        <a
          v-if="row.admin && row.admin !== 'none' && row.admin !== 'None' && row.admin !== '-'"
          href="#"
          class="admin-link"
          @click.prevent="openAdminPermissions(row)"
        >
          {{ row.admin }}
          <i class="material-icons admin-info-icon">info_outline</i>
        </a>
        <span v-else>{{ row.admin || '-' }}</span>
      </template>
      <template #col-peopleGroups="{ row }">
        <div v-if="row.peopleGroupList?.length" class="d-flex flex-wrap gap-1">
          <a
            v-for="group in row.peopleGroupList"
            :key="group.id"
            href="#"
            class="admin-link people-group-link"
            :title="`${group.name} · ${group.people_group_type} · ${group.member_count} member${group.member_count === 1 ? '' : 's'}`"
            @click.prevent.stop="openPeopleGroup(group)"
          >
            {{ group.name }}
            <i class="material-icons admin-info-icon">groups</i>
          </a>
        </div>
        <span v-else>-</span>
      </template>
      <template #col-mobilePhone="{ row }">
        <div v-if="row.mobilePhone" class="d-flex align-items-center gap-1">
          <span>{{ row.mobilePhone }}</span>
          <span class="badge sms-indicator" :class="row.smsEnabled ? 'bg-success' : 'bg-secondary'">
            SMS {{ row.smsEnabled ? 'Enabled' : 'Disabled' }}
          </span>
        </div>
        <span v-else>-</span>
      </template>

      <template #col-assignments="{ row }">
        <button @click="openAssignmentsPage(row)" class="btn btn-sm btn-outline-primary my-0">
          View
        </button>
      </template>

      <template #col-userSummary="{ row }">
        <button class="btn btn-sm btn-outline-success my-0" @click="openUserSummary(row)">
          <i class="material-icons text-sm align-middle">summarize</i>
        </button>
      </template>
      <template #col-volunteerImpact="{ row }">
        <button class="btn btn-sm btn-outline-primary my-0" title="View volunteer impact"
          @click="openVolunteerImpact(row)">
          <i class="material-icons text-sm align-middle">volunteer_activism</i>
          Impact
        </button>
      </template>

      <template #actions="{ row }">
        <button class="btn btn-sm my-0 py-0" @click="editUser(row)">
          <i class="material-icons text-lg text-orange">edit</i>
        </button>
        <button v-if="canShowDeleteButton" class="btn btn-sm my-0 py-0" @click="openDeleteModal(row)">
          <i class="material-icons text-lg text-danger">delete</i>
        </button>
      </template>
    </BaseTable>

    <!-- Delete confirmation modal -->
    <DeleteModal v-model:show="showDeleteModal"
      :itemName="selectedUser ? selectedUser.firstName + ' ' + selectedUser.lastName : ''"
      :confirmDisabled="deleteDisabled" :closeOnConfirm="false" @confirm="deleteUser">
      <template #details>
        <div v-if="assignmentPreviewLoading" class="text-muted" role="status">
          Loading future assignments...
        </div>
        <div v-else-if="assignmentPreviewError" class="alert alert-warning mb-0" role="alert">
          <p class="mb-2">{{ assignmentPreviewError }}</p>
          <button type="button" class="btn btn-sm btn-outline-dark mb-0" @click="loadDeletePreview">
            Retry
          </button>
        </div>
        <div v-else-if="futureAssignments.length === 0" class="text-muted">
          This user has no future assignments at this location.
        </div>
        <div v-else>
          <p class="mb-2">Choose which vacancies should be filled by running the auto-scheduler again:</p>
          <div v-for="assignment in futureAssignments" :key="assignment.ai_workgroup_id" class="form-check mb-2">
            <input
              :id="`delete-assignment-${assignment.ai_workgroup_id}`"
              v-model="rerunAiWorkgroupIds"
              class="form-check-input"
              type="checkbox"
              :value="Number(assignment.ai_workgroup_id)"
            >
            <label class="form-check-label" :for="`delete-assignment-${assignment.ai_workgroup_id}`">
              <strong>{{ assignment.activity_name }}</strong> — {{ assignment.workgroup_name }}
              <span class="d-block text-muted small">{{ assignment.start_datetime_formatted }}</span>
            </label>
          </div>
        </div>
      </template>
    </DeleteModal>

    <ContentModal v-model="showModal" title="User Preferences">
      <template #default>
        <div v-if="personPreferences">

          <div class="d-flex gap-8">
            <span class="d-flex gap-2">
              <h6 class="mb-0">Name:</h6>
              <p class="mb-0">{{ personPreferences.user_info?.name }}</p>
            </span>
            <span class="d-flex gap-2">
              <h6 class="mb-0">Email:</h6>
              <p class="mb-0">{{ personPreferences.user_info?.email }}</p>
            </span>
          </div>

          <h6 class="mt-4">Summary:</h6>
          <ul v-if="personPreferences.summary?.length" class="mb-0">
            <li v-for="(sentence, i) in personPreferences.summary" :key="'summary'+i">{{ sentence }}</li>
          </ul>
          <div v-else class="text-muted">No preference summary available.</div>

          <h6 class="mt-4">Skills:</h6>
          <div v-if="!personPreferences.skills || personPreferences.skills.length === 0" class="text-muted">No skills.</div>
          <div v-else class="d-flex flex-wrap gap-2">
            <MaterialSelectedBadge v-for="skill in personPreferences.skills" :key="skill.id"
              :label="`${skill.name} (Level ${skill.level})`" color="info" />
          </div>

          <h6 class="mt-4">Interests:</h6>
          <div v-if="!personPreferences.interest_skills || personPreferences.interest_skills.length === 0" class="text-muted">No interests.</div>
          <div v-else class="d-flex flex-wrap gap-2">
            <MaterialSelectedBadge v-for="interest in personPreferences.interest_skills" :key="interest.id"
              :label="interest.name" color="info" />
          </div>

          <h6 class="mt-4">Preferred Activities:</h6>
          <div v-if="personPreferences.activity_preferences?.interested_in_any_activity" class="text-muted">Interested in any activity.</div>
          <div v-else-if="!personPreferences.activity_preferences?.preferred_activities || personPreferences.activity_preferences.preferred_activities.length === 0" class="text-muted">No preferred activities.</div>
          <div v-else class="d-flex flex-wrap gap-2">
            <MaterialSelectedBadge v-for="activity in personPreferences.activity_preferences.preferred_activities" :key="activity.id"
              :label="activity.name" color="info" />
          </div>

          <h6 class="mt-4">Preferred Workgroups:</h6>
          <div v-if="personPreferences.workgroup_preferences?.interested_in_any_workgroup" class="text-muted">Interested in any workgroup.</div>
          <div v-else-if="!personPreferences.workgroup_preferences?.preferred_workgroups || personPreferences.workgroup_preferences.preferred_workgroups.length === 0" class="text-muted">No preferred workgroups.</div>
          <div v-else class="d-flex flex-wrap gap-2">
            <MaterialSelectedBadge v-for="workgroup in personPreferences.workgroup_preferences.preferred_workgroups" :key="workgroup.id"
              :label="workgroup.name" color="info" />
          </div>

          <h6 class="mt-4">Availability:</h6>
          <div v-if="Array.isArray(personPreferences.availability) && personPreferences.availability.length > 0">
            <ul>
              <li v-for="(item, i) in personPreferences.availability" :key="'av'+i">{{ item }}</li>
            </ul>
          </div>
          <div v-else-if="personPreferences.availability?.status" class="mb-2">
            <MaterialSelectedBadge :label="personPreferences.availability.status" color="success" />
          </div>
          <div v-else class="text-muted">No availability records.</div>

          <h6 class="mt-4">Scheduling Rules:</h6>
          <div v-if="(!personPreferences.scheduling_rules || personPreferences.scheduling_rules.length === 0) && (!personPreferences.availability?.rules || personPreferences.availability.rules?.length === 0)" class="text-muted">No scheduling rules.</div>
          <ul v-else>
            <li v-for="(rule, i) in (personPreferences.scheduling_rules || [])" :key="'sr'+i">
              {{ rule.description || rule.rule_description || rule.label || '-' }}
            </li>
            <li v-for="(rule, i) in (personPreferences.availability?.rules || [])" :key="'ar'+i">
              {{ rule.description || rule.rule_description || '-' }}
            </li>
          </ul>
        </div>
        <div v-else>
          <p>No preferences found.</p>
        </div>
      </template>
    </ContentModal>
    <ContentModal v-model="showPeopleGroupsModal" title="People Groups" size="2xl">
      <PeopleGroupsPanel v-if="showPeopleGroupsModal" />
    </ContentModal>
    <Modal v-if="isExternalModalOpen" :modalHeading="externalModalHeading" :iframeUrl="externalIframeUrl" size="2xl"
      refresh-on-close @close="closeExternalModal" />

    <!-- Edit User Modal -->
    <ContentModal v-model="showEditUserModal" :title="editingUserName" size="2xl">
      <template #default>
        <EditUsers v-if="showEditUserModal" :personId="editingUserId" @account-updated="handleAccountUpdate" />
      </template>
    </ContentModal>

    <!-- Admin Permissions Modal (lazy-fetches for the selected user) -->
    <AdminPermissionsModal
      v-model="showAdminPermissionsModal"
      :personName="adminPermissionsPersonName"
      locationName=""
      :personId="adminPermissionsPersonId"
      :locationId="locationId"
    />

    <SummaryModal v-model="showUserSummaryModal" :title="userSummaryTitle" :summaryList="userSummaryData"
      @open-people-group="openPeopleGroupById" />

    <!-- People Group details (opened from the People Groups column or the user summary) -->
    <ContentModal v-model="showPeopleGroupDetailModal" :title="peopleGroupDetailTitle" size="2xl">
      <PeopleGroupsPanel
        v-if="showPeopleGroupDetailModal && peopleGroupDetailId"
        :key="peopleGroupDetailId"
        :initial-group-id="peopleGroupDetailId"
        @group-closed="showPeopleGroupDetailModal = false"
      />
    </ContentModal>

    <!-- Volunteer Summary Modal (lazy-fetches for the selected user) -->
    <VolunteerSummaryModal
      v-model="showVolunteerSummaryModal"
      :personName="volunteerSummaryPersonName"
      locationName=""
      :personId="volunteerSummaryPersonId"
      :locationId="locationId"
    />
    <!-- Volunteer Impact Modal (lazy-fetches the selected user's authorized report) -->
    <ContentModal v-model="showVolunteerImpactModal" title="Volunteer Impact Report" size="2xl">
      <VolunteerImpactCard
        v-if="volunteerImpactPersonId"
        :person-id="volunteerImpactPersonId"
        :location-id="locationId"
        :person-name="volunteerImpactPersonName"
        :is-admin-view="true"
      />
    </ContentModal>

  </div>
</template>

<script setup>
import BaseTable from "@/components/Table/Table.vue";
import DeleteModal from "@/components/Modal/ConfirmationModal.vue";
import ContentModal from "@/components/Modal/ContentModal.vue";
import MaterialSelectedBadge from "@/components/common/MaterialSelectedBadge.vue";
import { ref, onMounted, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { getLocationId } from "../../utils/SessionDataHandler";
import MaterialButton from "../../components/common/MaterialButton.vue";
import OverlayLoader from "@/components/Loader/OverlayLoader.vue";
import { useToast } from "vue-toast-notification";
import Modal from "@/components/Modal/Modal.vue";
import AdminPermissionsModal from "@/components/AdminPermissions/AdminPermissionsModal.vue";
import VolunteerSummaryModal from "@/components/VolunteerSummary/VolunteerSummaryModal.vue";
import VolunteerImpactCard from "@/components/VolunteerImpact/VolunteerImpactCard.vue";
import { baseUrl } from "../../utils/BaseUrl.js";
import EditUsers from "./EditUsers.vue";
import SummaryModal from "@/components/Modal/SummaryModal.vue";
import { usePermissions } from "@/composables/usePermissions";
import { useBreadcrumb } from "@/composables/useBreadcrumb";
import BreadcrumbNav from "@/components/common/BreadcrumbNav.vue";
import PeopleGroupsPanel from "@/modules/people-groups/PeopleGroupsPanel.vue";

const router = useRouter();
const store = useStore();
const toast = useToast();
const { setTrail, clearTrail } = useBreadcrumb();

// Set top-level breadcrumb for this list page
clearTrail();
setTrail([
  { label: 'Manage Users', icon: 'people' },
]);
const {
  canAccessSetupWizard,
  isExactlyActivityInstanceAdmin,
  isExactlyActivityInstanceWorkgroupAdmin,
} = usePermissions();
// const route = useRoute();

const showDeleteModal = ref(false);
const selectedUser = ref(null);
const futureAssignments = ref([]);
const rerunAiWorkgroupIds = ref([]);
const assignmentPreviewLoading = ref(false);
const assignmentPreviewError = ref("");
const assignmentPreviewReady = ref(false);
const deleteInProgress = ref(false);
let previewRequestId = 0;
const showModal = ref(false);
const showPeopleGroupsModal = ref(false);
const showEditUserModal = ref(false);
const editingUserId = ref(null);
const editingUserName = ref('Edit User');
const showAdminPermissionsModal = ref(false);
const adminPermissionsPersonId = ref(null);
const adminPermissionsPersonName = ref('');
const showVolunteerSummaryModal = ref(false);
const volunteerSummaryPersonId = ref(null);
const volunteerSummaryPersonName = ref('');
const showVolunteerImpactModal = ref(false);
const volunteerImpactPersonId = ref(null);
const volunteerImpactPersonName = ref('');
const personPreferences = ref(null);
const isExternalModalOpen = ref(false);
const externalModalHeading = ref("");
const externalIframeUrl = ref("");

// const personId = Number(route.params.userid);
const locationId = getLocationId();

const loading = ref(false);
const loadModal = ref(false);
const searchTerm = ref("");

const pagination = computed(() => store.getters.manageUsersPagination);

// Instance / Instance Workgroup admins cannot delete users.
// Workgroup admins and above can (backend enforces detailed skill checks).
const canShowDeleteButton = computed(() => {
  if (isExactlyActivityInstanceAdmin.value || isExactlyActivityInstanceWorkgroupAdmin.value) {
    return false;
  }
  return true;
});
watch(showDeleteModal, (isOpen) => {
  if (!isOpen && !deleteInProgress.value) {
    previewRequestId++;
    selectedUser.value = null;
    futureAssignments.value = [];
    rerunAiWorkgroupIds.value = [];
    assignmentPreviewLoading.value = false;
    assignmentPreviewError.value = "";
    assignmentPreviewReady.value = false;
  }
});
const deleteDisabled = computed(() => (
  !assignmentPreviewReady.value
  || assignmentPreviewLoading.value
  || Boolean(assignmentPreviewError.value)
  || deleteInProgress.value
));

const columns = [
  { key: "id", label: "ID" },
  { key: "firstName", label: "FIRSTNAME" },
  { key: "lastName", label: "LASTNAME" },
  { key: "email", label: "EMAIL" },
  { key: "mobilePhone", label: "MOBILE PHONE" },
  { key: "volunteer", label: "VOLUNTEER" },
  { key: "schedulingPreferences", label: "SCHEDULING PREFERENCES" },
  { key: "admin", label: "ADMIN" },
  { key: "peopleGroups", label: "PEOPLE GROUPS" },
  { key: "assignmentcount", label: "ASSIGNMENT COUNT" },
  { key: "userSummary", label: "SUMMARY" },
  { key: "volunteerImpact", label: "VOLUNTEER IMPACT" },
  { key: "assignments", label: "Assignments" },
  { key: "lastLogon", label: "LAST LOGON" },
  { key: "scheduleInfo", label: "SCHEDULE INFO" },
];

const users = computed(() => store.getters.allManageUsers);

// Filters
const filterAdmin = ref('');
const filterVolunteer = ref('');

const filteredUsers = computed(() => users.value);

const clearFilters = () => {
  filterAdmin.value = '';
  filterVolunteer.value = '';
};

// Re-fetch from page 1 when filters change
watch([filterAdmin, filterVolunteer], () => {
  fetchUsers(1);
});

// ✅ Fetch users with pagination and search
const fetchUsers = async (page = 1, pageSize = pagination.value.page_size || 10) => {
  loading.value = true;
  try {
    await store.dispatch("fetchManageUsers", {
      locationId,
      search: searchTerm.value,
      page,
      pageSize,
      isAdmin: filterAdmin.value,
      isVolunteer: filterVolunteer.value,
    });
  } finally {
    loading.value = false;
  }
};

// 🧠 Initial load
onMounted(() => fetchUsers());

// ✅ Pagination handlers
const onChangePage = async (page) => {
  await fetchUsers(page);
};

const onUpdatePageSize = async (size) => {
  await fetchUsers(1, size);
};

// ✅ Search handler
const onSearchChanged = async (term) => {
  searchTerm.value = term;
  await fetchUsers(1);
};

const openExternalModal = (heading, url) => {
  if (!url) {
    toast.warning(`Unable to open ${heading || "this page"}`, {
      position: "top-right",
    });
    return;
  }
  externalModalHeading.value = heading;
  externalIframeUrl.value = url;
  isExternalModalOpen.value = true;
};

const closeExternalModal = () => {
  isExternalModalOpen.value = false;
  externalModalHeading.value = "";
  externalIframeUrl.value = "";
  refreshTable();
};

const refreshTable = () => {
  fetchUsers(pagination.value.current_page || 1);
};

const openAddUserPage = () => {
  openExternalModal("Add User", `${baseUrl}/MF/cs-mf-addusers/`);
};

const openSetupWizardPage = () => {
  openExternalModal("Setup Wizard", `${baseUrl}/MF/cs-mf-setupwizard/`);
};

const openAssignmentsPage = (row) => {
  const fullName = row ? `${row.firstName || ""} ${row.lastName || ""}`.trim() : "Assignments";
  const label = `${fullName} - Assignments`;
  openExternalModal(label, row?.assignments);
};

// ✅ Other handlers
const openDeleteModal = (user) => {
  selectedUser.value = user;
  futureAssignments.value = [];
  rerunAiWorkgroupIds.value = [];
  assignmentPreviewError.value = "";
  assignmentPreviewReady.value = false;
  deleteInProgress.value = false;
  showDeleteModal.value = true;
  loadDeletePreview();
};

const loadDeletePreview = async () => {
  const personId = selectedUser.value?.id;
  const requestId = ++previewRequestId;
  assignmentPreviewLoading.value = true;
  assignmentPreviewError.value = "";
  assignmentPreviewReady.value = false;

  if (!personId) {
    assignmentPreviewLoading.value = false;
    assignmentPreviewError.value = "Unable to load this user's future assignments.";
    return;
  }

  try {
    const assignments = await store.dispatch("fetchFutureAssignments", {
      personId,
      locationId,
    });
    if (requestId !== previewRequestId || selectedUser.value?.id !== personId) return;

    futureAssignments.value = assignments;
    rerunAiWorkgroupIds.value = assignments.map((assignment) => Number(assignment.ai_workgroup_id));
    assignmentPreviewReady.value = true;
  } catch (error) {
    if (requestId !== previewRequestId || selectedUser.value?.id !== personId) return;
    assignmentPreviewError.value = error?.response?.data?.message
      || error?.message
      || "Could not load future assignments. Retry before deleting this user.";
  } finally {
    if (requestId === previewRequestId) {
      assignmentPreviewLoading.value = false;
    }
  }
};

const editUser = (row) => {
  const userName = row ? `${row.firstName || ''} ${row.lastName || ''}`.trim() : 'Edit User';
  editingUserId.value = row.id;
  editingUserName.value = userName || `User #${row.id}`;
  showEditUserModal.value = true;
};
const handleAccountUpdate = async (result) => {
  if (result?.shouldLeaveEditor) {
    showEditUserModal.value = false;
    editingUserId.value = null;
    return;
  }

  await fetchUsers(pagination.value.currentPage || 1);
};

watch(showEditUserModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    refreshTable();
  }
});

const openSchedule = async (row) => {
  loadModal.value = true;
  if (!row || !row.id) {
    loadModal.value = false;
    return;
  }
  const res = await store.dispatch("fetchPersonPreferences", {
    personId: row.id,
    locationId,
  });
  personPreferences.value = res?.preferences || null;
  showModal.value = true;
  loadModal.value = false;
};

const deleteUser = async () => {
  if (!selectedUser.value || deleteDisabled.value) return;

  try {
    deleteInProgress.value = true;
    const response = await store.dispatch("deleteUser", {
      personId: selectedUser.value.id,
      locationId: locationId,
      rerunAiWorkgroupIds: rerunAiWorkgroupIds.value,
    });

    const message = response?.message || "User deleted successfully";
    toast.success(message, { position: "top-right" });
    showDeleteModal.value = false;
    selectedUser.value = null;

    // Refresh the users list
    await fetchUsers(pagination.value.currentPage || 1);

  } catch (error) {
    console.error("Error deleting user:", error);
    if (error?.response?.status === 409) {
      assignmentPreviewReady.value = false;
      assignmentPreviewError.value = error.response.data?.message
        || "The assignments changed since the preview loaded. Reload the preview before deleting.";
    }
    // Error is already handled by handleApiError in the store action
  } finally {
    deleteInProgress.value = false;
  }
};

const redirectToUsersSummary = () => {
  router.push({ name: "UsersSummary" });
};

const showUserSummaryModal = ref(false);
const userSummaryData = ref([]);
const userSummaryTitle = ref('');

const showPeopleGroupDetailModal = ref(false);
const peopleGroupDetailId = ref(null);
const peopleGroupDetailTitle = ref('People Group');

const openPeopleGroup = (group) => {
  if (!group?.id) return;
  peopleGroupDetailId.value = group.id;
  peopleGroupDetailTitle.value = group.name ? `People Group: ${group.name}` : 'People Group';
  showPeopleGroupDetailModal.value = true;
};

const openPeopleGroupById = (groupId) => {
  const group = users.value
    .flatMap((user) => user.peopleGroupList || [])
    .find((candidate) => Number(candidate.id) === Number(groupId));
  openPeopleGroup(group || { id: groupId });
};

// Membership may change inside the group details, so refresh the list when they close
watch(showPeopleGroupDetailModal, (isOpen, wasOpen) => {
  if (wasOpen && !isOpen) {
    peopleGroupDetailId.value = null;
    refreshTable();
  }
});

// Summary text is rendered as HTML, so group names (user-supplied) must be escaped first
const escapeSummaryText = (value) => String(value ?? '')
  .replace(/\*/g, '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const peopleGroupSummaryItems = (row) => {
  const groups = row.peopleGroupList || [];
  if (!groups.length) {
    return [{ icon: 'groups', category: 'People Groups', text: 'Not a member of any People Groups at this Location.' }];
  }
  return groups.map((group) => ({
    icon: 'groups',
    category: 'People Groups',
    people_group_id: group.id,
    text: `**${escapeSummaryText(group.name)}** · ${escapeSummaryText(group.people_group_type)} · ${group.member_count} member${group.member_count === 1 ? '' : 's'}${group.is_group_admin ? ' · Group admin' : ''}`,
  }));
};

const openUserSummary = (row) => {
  const name = `${row.firstName || ''} ${row.lastName || ''}`.trim();
  userSummaryTitle.value = `Summary: ${name}`;
  userSummaryData.value = [
    { icon: 'person', category: 'General', text: `**${name}** (${row.email}).` },
    { icon: 'badge', category: 'General', text: `Volunteer status: **${row.volunteer || 'N/A'}**.` },
    { icon: 'admin_panel_settings', category: 'General', text: `Admin status: **${row.admin || 'N/A'}**.` },
    { icon: 'phone_android', category: 'Notifications', text: row.mobilePhone ? `Mobile: **${row.mobilePhone}** (SMS ${row.smsEnabled ? 'enabled' : 'disabled'}).` : 'No mobile phone configured.' },
    { icon: 'event', category: 'Scheduling', text: `Scheduling preferences: **${row.schedulingPreferences || 'N/A'}**.` },
    { icon: 'assignment', category: 'Assignments', text: `Assignment count: **${row.assignmentcount || 0}**.` },
    { icon: 'login', category: 'Activity', text: `Last logon: **${row.lastLogon || 'Never'}**.` },
    { icon: 'calendar_today', category: 'Activity', text: `Last volunteer date: **${row.scheduleInfo || 'N/A'}**.` },
    ...peopleGroupSummaryItems(row),
  ];
  showUserSummaryModal.value = true;
};

const openAdminPermissions = (row) => {
  adminPermissionsPersonId.value = row.id;
  adminPermissionsPersonName.value = `${row.firstName || ''} ${row.lastName || ''}`.trim();
  showAdminPermissionsModal.value = true;
};

const openVolunteerSummary = (row) => {
  volunteerSummaryPersonId.value = row.id;
  volunteerSummaryPersonName.value = `${row.firstName || ''} ${row.lastName || ''}`.trim();
  showVolunteerSummaryModal.value = true;
};
const openVolunteerImpact = (row) => {
  if (!row?.id) return;
  volunteerImpactPersonId.value = row.id;
  volunteerImpactPersonName.value = `${row.firstName || ''} ${row.lastName || ''}`.trim();
  showVolunteerImpactModal.value = true;
};

</script>

<style scoped>
.btn-refresh {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 6px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.btn-refresh:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.55);
}
.btn-refresh:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
}
.admin-link {
  color: #344767;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  cursor: pointer;
  font-size: 0.875rem;
}
.admin-link:hover {
  text-decoration: underline;
  color: #667eea;
}
.admin-info-icon {
  font-size: 14px;
  opacity: 0.5;
  transition: opacity 0.15s;
}
.admin-link:hover .admin-info-icon {
  opacity: 1;
  color: #667eea;
}
.people-group-link {
  background: #eef2ff;
  border-radius: 12px;
  padding: 1px 8px;
  font-size: 0.78rem;
}
.filter-select {
  max-width: 160px;
  border-radius: 10px;
  border: 1px solid #dee2e6;
  font-size: 0.82rem;
}
.sms-indicator {
  font-size: 0.67rem;
  font-weight: 600;
}
</style>
